<script setup lang="ts">
// ツールガイド: 今選んでいるツールの使い方だけを出す小さなパネル。
// 非モーダル (開いたまま操作できる)。ツールを切り替えると中身も切り替わる。
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useUiStore } from '@/stores/uiStore'
import { useI18n, type I18nKey } from '@/i18n'
import { TOOL_KEYS } from '@/composables/useShortcuts'
import { guideFor, tx } from '@/help/toolGuides'

const emit = defineEmits<{ close: [] }>()
const ui = useUiStore()
const { t } = useI18n()
const guide = computed(() => guideFor(ui.effectiveTool))
const toolName = computed(() => t(`tool.${ui.effectiveTool}` as I18nKey))
const toolKey = computed(() => Object.entries(TOOL_KEYS).find(([, v]) => v === ui.effectiveTool)?.[0]?.toUpperCase() ?? '')

// カンバスの左上 (オプションバーの下、ツールバーの右) に置く
const pos = ref({ top: 80, left: 56 })
function place() {
  const main = document.querySelector('.main')?.getBoundingClientRect()
  const bar = document.querySelector('.toolbar')?.getBoundingClientRect()
  if (!main) return
  pos.value = { top: Math.round(main.top + 8), left: Math.round((bar?.right ?? main.left + 44) + 8) }
}
function onKey(e: KeyboardEvent) {
  if (e.key === 'Escape') {
    e.preventDefault()
    emit('close')
  }
}
onMounted(() => {
  place()
  window.addEventListener('resize', place)
  window.addEventListener('keydown', onKey)
})
onBeforeUnmount(() => {
  window.removeEventListener('resize', place)
  window.removeEventListener('keydown', onKey)
})
</script>

<template>
  <div class="guide" :style="{ top: pos.top + 'px', left: pos.left + 'px' }">
    <div class="head">
      <span class="name">{{ toolName }}</span>
      <kbd v-if="toolKey">{{ toolKey }}</kbd>
      <span class="spacer" />
      <button class="ghost tiny" :title="t('guide.close')" @click="emit('close')">✕</button>
    </div>
    <p class="summary">{{ tx(guide.summary) }}</p>
    <section>
      <h3>{{ t('guide.steps') }}</h3>
      <ol>
        <li v-for="(s, i) in guide.steps" :key="i">{{ tx(s) }}</li>
      </ol>
    </section>
    <section v-if="guide.keys?.length">
      <h3>{{ t('guide.keys') }}</h3>
      <dl>
        <template v-for="(k, i) in guide.keys" :key="i">
          <dt><kbd>{{ k.key }}</kbd></dt>
          <dd>{{ tx(k.desc) }}</dd>
        </template>
      </dl>
    </section>
    <section v-if="guide.tip">
      <h3>{{ t('guide.tip') }}</h3>
      <p class="tip">{{ tx(guide.tip) }}</p>
    </section>
    <div class="foot">
      <span class="muted">{{ t('guide.switchHint') }}</span>
      <button class="ghost link" @click="ui.dialog = 'shortcuts'">{{ t('guide.allShortcuts') }}</button>
    </div>
  </div>
</template>

<style scoped>
.guide {
  position: fixed;
  z-index: 40;
  width: 330px;
  max-height: calc(100vh - 160px);
  overflow: auto;
  background: var(--bg-1);
  border: 1px solid var(--line-strong);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow);
  padding: 10px 12px 8px;
  font-size: 12px;
}
.head {
  display: flex;
  align-items: center;
  gap: 6px;
}
.name {
  font-weight: 600;
}
.spacer {
  flex: 1;
}
.tiny {
  width: 22px;
  height: 20px;
  padding: 0;
}
.summary {
  margin: 6px 0 10px;
  color: var(--fg-1);
}
section {
  margin-bottom: 10px;
}
h3 {
  font-size: 11px;
  font-weight: 600;
  color: var(--fg-2);
  margin: 0 0 4px;
  letter-spacing: 0.04em;
}
ol {
  margin: 0;
  padding-left: 18px;
}
ol li {
  margin-bottom: 3px;
}
dl {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 3px 8px;
  margin: 0;
}
dt {
  white-space: nowrap;
}
dd {
  margin: 0;
}
.tip {
  margin: 0;
  color: var(--fg-1);
}
.foot {
  display: flex;
  align-items: center;
  gap: 8px;
  border-top: 1px solid var(--line-weak);
  padding-top: 6px;
  font-size: 10px;
}
.foot .muted {
  flex: 1;
}
.link {
  text-decoration: underline;
  padding: 0 2px;
  height: 18px;
  font-size: 10px;
}
kbd {
  font-size: 10px;
}
</style>
